import React, { useState } from "react";
import { useForm } from "react-hook-form";

// region Imports - Assets
import Logotipo from "@assets/company/Logotipo.svg";
import LogotipoMinifiedColor from "@assets/company/LogotipoMinifiedColor.svg";
import LogotipoMinifiedWhite from "@assets/company/LogotipoMinifiedWhite.svg";
// endregion Imports - Assets
// region Imports - Libraries
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
// endregion Imports - Libraries
// region Imports - Helpers
import utils from "@helpers/utils";
// endregion Imports - Helpers
// region Imports - GraphQL
import { useMutation } from "@apollo/client";
import {
  ECommandResultStatus,
  AssetsMutationUploadIconsArgs
} from "@graphql/helpers/types.graphql";
import { TUploadIconAssetsMutations, uploadIconAssets } from "@graphql/groups/assets/mutation.graphql";
// endregion Imports - GraphQL
// region Imports - Atoms
import Loading from "@atoms/Loading";
import ImageUploaderSquare from "src/molecules/ImageUploaderSquare";
// endregion Imports - Atoms
// region Imports - Constants
// endregion Imports - Constants
// region Imports - Language
import { GlobalMessages, SettingMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Imports - Language
// region Imports - Styles
import Styled from "./styles";
// endregion Imports - Styles

// region Interfaces
type IconTypes = "favicon" | "logo" | "logo192" | "logotipo" | "logotipoMinifiedColor" | "logotipoMinifiedWhite";

interface IConfigurableIcons {
  favicon?: File
  logo?: File
  logo192?: File
  logotipo?: File
  logotipoMinifiedColor?: File
  logotipoMinifiedWhite?: File
}
// endregion Interfaces

const FormEnvIcons: React.FC = () => {

  // region States
  const [
    configurableIcons, setConfigurableIcons
  ] = useState<IConfigurableIcons>({});

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // endregion States
  // region Hooks
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { handleSubmit } = useForm<IConfigurableIcons>();
  // endregion Hooks
  /* region GraphQL */
  const [uploadAssets] = useMutation<TUploadIconAssetsMutations, AssetsMutationUploadIconsArgs>(uploadIconAssets());
  /* endregion GraphQL */
  // region Functions

  const handleIconChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    icon: IconTypes
  ) => {

    const currentFile = event.target.files?.[0];

    if (!currentFile) return;

    setConfigurableIcons((prevState) => ({
      ...prevState,
      [icon]: currentFile
    }));
  };

  /*
    * Update, if necessary, the icon files
   */
  const handleSubmitForm = async () => {

    try {

      setIsLoading(true);

      const hasUpdated = Object.values(configurableIcons).some((icon) => icon !== undefined);

      if (!hasUpdated) {
        addToast(
          {
            title: t(GlobalMessages.warningText),
            description: t(SettingMessages.settingIconsNoChange),
            type: "info"
          }
        );

        return;
      }

      const { data } = await uploadAssets({
        variables: configurableIcons
      });

      if (data?.assets?.uploadIcons?.status === ECommandResultStatus.SUCCESS) {

        if ("caches" in window) {
          const cacheKeys = await caches.keys();

          await Promise.allSettled(
            cacheKeys.map((name) => caches.delete(name))
          );
        }

        window.location.reload();

      } else {
        addToast({
          title: t(GlobalMessages.errorText),
          description: data?.assets?.uploadIcons?.message,
          type: "error"
        });
      }

    } catch (error) {
      addToast(
        {
          title: `${t(GlobalMessages.errorText)}!`,
          description: error.message,
          type: "error"
        }
      );
    } finally {
      setIsLoading(false);
      setConfigurableIcons({});
    }
  };

  const handleHelp = (icon: IconTypes) => {

    let docHelperUrl : string;

    // get doc helper url by icon type
    switch (icon) {
      case "favicon":
        docHelperUrl = "https://www.notion.so/telluria/favicon-ico-b5e65e6436dc4d78be102e00df7ddf02";
        break;
      case "logo":
        docHelperUrl = "https://www.notion.so/telluria/logo-png-a679a103221e4c53848502fdc702238e";
        break;
      case "logo192":
        docHelperUrl = "https://www.notion.so/telluria/logo192-png-89d93057203242c6afbd0ada289dfbeb";
        break;
      case "logotipo":
        docHelperUrl = "https://www.notion.so/telluria/Logotipo-svg-97da2e80c38b4597adb105e99da8a278";
        break;
      case "logotipoMinifiedColor":
        docHelperUrl = "https://www.notion.so/telluria/LogotipoMinifiedColor-svg-eac21d0a69b84612b78b47263e847cb7";
        break;
      case "logotipoMinifiedWhite":
        docHelperUrl = "https://www.notion.so/telluria/LogotipoMinifiedWhite-svg-8c7e9a000f944a42bf4786d3eef2612a";
        break;
      default:
        docHelperUrl = "";
        break;
    }

    window.open(docHelperUrl, "_blank");

  };

  // endregion Functions

  return (
    <Styled.FormEnvIconsContainer>
      <Loading loading={isLoading} />
      {!isLoading && (

        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <Styled.ParamItem>
            <Styled.ParamItemText>favicon.ico</Styled.ParamItemText>
            <ImageUploaderSquare
              allowedExtension=".ico"
              imgPath={`${utils.getBackendBaseUrl(false)}/visual/favicon.ico?t=${Date.now()}`}
              imgFallbackPath={`${process.env.PUBLIC_URL}/favicon.ico`}
              handleEdit={(event) => handleIconChange(event, "favicon")}
              handleHelp={() => handleHelp("favicon")}
            />
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>logo.png</Styled.ParamItemText>
            <ImageUploaderSquare
              allowedExtension=".png"
              imgPath={`${utils.getBackendBaseUrl(false)}/visual/logo.png?t=${Date.now()}`}
              imgFallbackPath={`${process.env.PUBLIC_URL}/icons/logo.png`}
              handleEdit={(event) => handleIconChange(event, "logo")}
              handleHelp={() => handleHelp("logo")}
            />
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>logo192.png</Styled.ParamItemText>
            <ImageUploaderSquare
              allowedExtension=".png"
              imgPath={`${utils.getBackendBaseUrl(false)}/visual/logo192.png?t=${Date.now()}`}
              imgFallbackPath={`${process.env.PUBLIC_URL}/logo192.png`}
              handleEdit={(event) => handleIconChange(event, "logo192")}
              handleHelp={() => handleHelp("logo192")}
            />
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>Logotipo.svg</Styled.ParamItemText>
            <ImageUploaderSquare
              allowedExtension=".svg"
              imgPath={`${utils.getBackendBaseUrl(false)}/visual/Logotipo.svg?t=${Date.now()}`}
              imgFallbackPath={Logotipo}
              handleEdit={(event) => handleIconChange(event, "logotipo")}
              handleHelp={() => handleHelp("logotipo")}
            />
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>LogotipoMinifiedColor.svg</Styled.ParamItemText>
            <ImageUploaderSquare
              allowedExtension=".svg"
              imgPath={`${utils.getBackendBaseUrl(false)}/visual/LogotipoMinifiedColor.svg?t=${Date.now()}`}
              imgFallbackPath={LogotipoMinifiedColor}
              handleEdit={(event) => handleIconChange(event, "logotipoMinifiedColor")}
              handleHelp={() => handleHelp("logotipoMinifiedColor")}
            />
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>LogotipoMinifiedWhite.svg</Styled.ParamItemText>
            <ImageUploaderSquare
              allowedExtension=".svg"
              imgPath={`${utils.getBackendBaseUrl(false)}/visual/LogotipoMinifiedWhite.svg?t=${Date.now()}`}
              imgFallbackPath={LogotipoMinifiedWhite}
              handleEdit={(event) => handleIconChange(event, "logotipoMinifiedWhite")}
              colorImgBackground="black"
              handleHelp={() => handleHelp("logotipoMinifiedWhite")}
            />
          </Styled.ParamItem>
          <Styled.SubmitContainer>
            <Styled.SubmitButton className="confirmButton" type="submit" disabled={isLoading}>
              {t(GlobalMessages.buttonSaveChanges)}
            </Styled.SubmitButton>
            <Styled.ReloadSubmitInfoSpan>
              **{t(SettingMessages.settingNotificationReload)}
            </Styled.ReloadSubmitInfoSpan>
          </Styled.SubmitContainer>
        </form>
      )}
    </Styled.FormEnvIconsContainer>
  );
};

export default FormEnvIcons;
